import { atom } from 'jotai';
import dayjs from 'dayjs';
import en from 'dayjs/locale/en';

import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);
import weekOfYear from 'dayjs/plugin/weekOfYear';
dayjs.extend(weekOfYear);
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);

// Atoms
import { datepickerAtom } from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';

// lib
import { periodsTranslation } from '@/lib/periods';

// Utils
import { checkDateWithMinMax, getDatePeriods } from '@/utils/datepicker/periods';

// Types
import type { DatepickerPeriodsAvailableType } from '@/lib/periods';
import type { DatepickerRangeAtomImmer } from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';
import type { Dayjs } from 'dayjs';

export const updatePeriodAtom = atom(null, (get, set, period: DatepickerPeriodsAvailableType) => {
    const datepickerData = get(datepickerAtom as DatepickerRangeAtomImmer);
    const minDate = datepickerData.settings?.minDate;
    const maxDate = datepickerData.settings?.maxDate;
    const timezone = datepickerData.settings.timezone;

    dayjs.locale({
        ...en,
        weekStart: datepickerData.dayPickerOptions?.weekStartsOn ?? 1
    });

    const currentDayTz = maxDate ? dayjs(maxDate) : dayjs().utc().tz(timezone);
    const minDateTz = minDate ? dayjs(minDate) : null;
    const fromDateTz = dayjs(datepickerData.date.from);
    const toDateTz = dayjs(datepickerData.date.to);

    if (period === 'custom') {
        // If there is a minDate => check if the start date is lower than minDate
        const startDate = checkDateWithMinMax({ currentDate: fromDateTz, minDate, maxDate });
        const endDate = checkDateWithMinMax({ currentDate: toDateTz, minDate, maxDate });

        // Change dates selected
        return set(datepickerAtom as DatepickerRangeAtomImmer, (draft) => {
            draft.date = {
                from: startDate.startOf('day').toDate(),
                to: endDate.endOf('day').toDate()
            };
            draft.period = 'custom';
        });
    }

    const rangeDates = getDatePeriods({ minDateTz, period, currentDayTz });
    // Check if current period is disabled and change to last 30 days
    const isCurrentPeriodDisabled = minDate && minDateTz.isAfter(rangeDates.from);
    const newPeriod: DatepickerPeriodsAvailableType = isCurrentPeriodDisabled
        ? 'last_30_days'
        : period;

    // Change dates selected
    const newRangeDate = getDatePeriods({
        period: newPeriod,
        minDateTz,
        currentDayTz
    });

    const startDate = checkDateWithMinMax({ currentDate: newRangeDate.from, minDate, maxDate });
    const endDate = checkDateWithMinMax({ currentDate: newRangeDate.to, minDate, maxDate });

    set(datepickerAtom as DatepickerRangeAtomImmer, (draft) => {
        draft.date = {
            from: startDate.startOf('day').toDate(),
            to: endDate.endOf('day').toDate()
        };
        draft.period = newPeriod;
    });
});

export const getPeriodsInfosAtom = atom((get) => {
    const datepickerData = get(datepickerAtom as DatepickerRangeAtomImmer);
    const minDate = datepickerData.settings?.minDate;
    const maxDate = datepickerData.settings?.maxDate;
    const timezone = datepickerData.settings.timezone;

    dayjs.locale({
        ...en,
        weekStart: datepickerData.dayPickerOptions?.weekStartsOn ?? 1
    });

    const currentDayTz = maxDate ? dayjs(maxDate) : dayjs().utc().tz(timezone);
    const minDateTz = minDate ? dayjs(minDate) : null;

    const periodsList = Object.keys(periodsTranslation);

    return periodsList.reduce(
        (acc, period) => {
            const rangeDates = getDatePeriods({ minDateTz, period, currentDayTz });
            const isCurrentPeriodDisabled = Boolean(
                minDate && rangeDates?.from && minDateTz.isAfter(rangeDates.from)
            );

            return {
                ...acc,
                [period]: {
                    date: rangeDates,
                    isDisabled: isCurrentPeriodDisabled
                }
            };
        },
        {} as Record<
            DatepickerPeriodsAvailableType,
            { date: { from: Dayjs; to: Dayjs }; isDisabled: boolean }
        >
    );
});
